import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { Currency, useQuery } from '@sequencehq/api/utils'
import { MINUTES, SECONDS } from '@sequencehq/utils/dates'
import { useMemo } from 'react'
import { indexSyncByMonth, type XeroMonthSync } from './deriveMonthSyncState'

// Poll quickly while a push is in flight so progress feels live, then back off to
// a heartbeat once every month has settled — mirrors the bulk-actions in-flight
// polling cadence.
const ACTIVE_REFETCH_INTERVAL = 3 * SECONDS
const IDLE_REFETCH_INTERVAL = 5 * MINUTES

export type UseXeroSyncStatusResult = {
  /** Sync state indexed by yyyy-MM month key. */
  byMonthKey: Map<string, XeroMonthSync>
  isLoading: boolean
}

/**
 * Reads each month's Xero recognition push status for the given currency and
 * derives its sync state. Polls while any month is mid-push so a push started
 * elsewhere (e.g. the Push to Xero modal) animates to completion without a manual
 * refresh.
 */
export const useXeroSyncStatus = (params: {
  currency: Currency
  enabled?: boolean
}): UseXeroSyncStatusResult => {
  const query = useQuery(
    dashboardv99990101Client.getJournalPushXeroSyncStatus,
    { currency: params.currency },
    {
      enabled: params.enabled ?? true,
      refetchInterval: currentQuery => {
        const isPushInFlight = (currentQuery.state.data?.months ?? []).some(
          month => month.posting > 0 || month.pending > 0
        )
        return isPushInFlight ? ACTIVE_REFETCH_INTERVAL : IDLE_REFETCH_INTERVAL
      }
    }
  )

  const byMonthKey = useMemo(
    () => indexSyncByMonth(query.data?.months ?? []),
    [query.data?.months]
  )

  return {
    byMonthKey,
    isLoading: query.isPending
  }
}
